import React, { useEffect, useState } from 'react';
import { configAPI } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Save, GraduationCap, CheckCircle2, AlertCircle } from 'lucide-react';
import { isAdmin } from '@/lib/auth';

export default function Settings() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  // Integração Classroom (institucional)
  const [gcStatus, setGcStatus] = useState({ loading: true });
  const [gcConnecting, setGcConnecting] = useState(false);

  useEffect(() => {
    loadConfig();
    refreshClassroomStatus();
    // Pós-OAuth: detecta volta do callback e reconsulta status
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('classroom_connected') === '1') {
        toast.success('Conta Google conectada. Validando escopos...', { duration: 6000 });
        params.delete('classroom_connected');
        const newSearch = params.toString();
        window.history.replaceState({}, '',
          window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash);
        // dá tempo do callback persistir
        setTimeout(refreshClassroomStatus, 800);
      }
    } catch (_) { /* noop */ }
  }, []);

  const refreshClassroomStatus = async () => {
    try {
      const r = await api.get('/google/classroom/status');
      setGcStatus({ loading: false, ...r.data });
    } catch (_) {
      setGcStatus({ loading: false, google_connected: false, classroom_authorized: false });
    }
  };

  const conectarClassroom = async () => {
    setGcConnecting(true);
    try {
      const ret = '/settings';
      const res = await api.get(`/classroom/connect?force=1&return_to=${encodeURIComponent(ret)}`);
      const url = res.data?.authorization_url;
      if (!url) {
        toast.error('Não foi possível iniciar a autorização.');
        setGcConnecting(false);
        return;
      }
      // Same-tab redirect — preserva sessão DNACore
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao iniciar autorização.');
      setGcConnecting(false);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await configAPI.get();
      setConfig(response.data);
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await configAPI.update(config);
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  if (loading) {
    return <div className="p-6">Carregando...</div>;
  }

  if (!isAdmin()) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground">Apenas administradores podem acessar as configurações do sistema.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-3">
        <SettingsIcon className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Configurações</h1>
          <p className="text-muted-foreground mt-1">Parâmetros do sistema</p>
        </div>
      </div>

      <form onSubmit={handleSave}>
        <div className="grid gap-6 max-w-3xl">
          <Card className="border-border/60">
            <CardHeader>
              <CardTitle>Taxas e Juros</CardTitle>
              <CardDescription>Configurações de multa e juros por atraso</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Multa por Atraso (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={config.fine_percentage}
                  onChange={(e) => setConfig({ ...config, fine_percentage: parseFloat(e.target.value) })}
                  data-testid="fine-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Percentual aplicado sobre o valor original</p>
              </div>
              <div>
                <Label>Juros por Dia (% ao dia)</Label>
                <Input
                  type="number"
                  step="0.001"
                  value={config.interest_rate_per_day}
                  onChange={(e) => setConfig({ ...config, interest_rate_per_day: parseFloat(e.target.value) })}
                  data-testid="interest-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Juros calculados por dia de atraso</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60">
            <CardHeader>
              <CardTitle>Dados Bancários</CardTitle>
              <CardDescription>Informações para geração de remessa</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Código do Banco</Label>
                <Input
                  value={config.bank_code}
                  onChange={(e) => setConfig({ ...config, bank_code: e.target.value })}
                  disabled
                />
              </div>
              <div>
                <Label>Agência</Label>
                <Input
                  value={config.agency}
                  onChange={(e) => setConfig({ ...config, agency: e.target.value })}
                />
              </div>
              <div>
                <Label>Conta</Label>
                <Input
                  value={config.account}
                  onChange={(e) => setConfig({ ...config, account: e.target.value })}
                />
              </div>
              <div>
                <Label>Nome do Beneficiário</Label>
                <Input
                  value={config.beneficiary_name}
                  onChange={(e) => setConfig({ ...config, beneficiary_name: e.target.value })}
                />
              </div>
              <div>
                <Label>CNPJ do Beneficiário</Label>
                <Input
                  value={config.beneficiary_document}
                  onChange={(e) => setConfig({ ...config, beneficiary_document: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60" data-testid="integracao-classroom-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-purple-600" />
                Google Classroom — Conta Institucional
              </CardTitle>
              <CardDescription>
                Conecte a conta Google institucional UMA vez. Todos os envios de
                recuperação ao Classroom usarão essa conta — professores não precisam autorizar individualmente.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {gcStatus.loading ? (
                <p className="text-sm text-muted-foreground">Verificando status...</p>
              ) : gcStatus.classroom_authorized ? (
                <div className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-green-700">Conta institucional conectada</div>
                    <div className="text-muted-foreground">
                      {gcStatus.google_email && <>Email: <code className="bg-muted px-1 rounded">{gcStatus.google_email}</code> · </>}
                      Escopos Classroom OK.
                    </div>
                  </div>
                </div>
              ) : gcStatus.google_connected ? (
                <div className="flex items-start gap-2 text-sm">
                  <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                  <div>
                    <div className="font-semibold text-orange-700">Google conectado, mas Classroom não autorizado</div>
                    <div className="text-muted-foreground">
                      Faltam {gcStatus.missing_scopes?.length || 0} escopos. Reconecte marcando todas as permissões.
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Nenhuma conta institucional conectada ainda.</p>
              )}
              <Button
                type="button"
                onClick={conectarClassroom}
                disabled={gcConnecting}
                variant={gcStatus.classroom_authorized ? 'outline' : 'default'}
                className={gcStatus.classroom_authorized ? '' : 'bg-purple-600 hover:bg-purple-700 text-white'}
                data-testid="conectar-classroom-institucional-btn"
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                {gcConnecting
                  ? 'Redirecionando...'
                  : gcStatus.classroom_authorized
                    ? 'Reconectar Conta Institucional'
                    : 'Conectar Conta Institucional'}
              </Button>
            </CardContent>
          </Card>

          <Button type="submit" size="lg" className="w-full max-w-md" data-testid="save-settings-button">
            <Save className="w-4 h-4 mr-2" />
            Salvar Configurações
          </Button>
        </div>
      </form>
    </div>
  );
}